/**
 * SeatSheet.tsx - Edit delivery seat (section / row / seat)
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { authService } from '../../services/customerApi';
import { useVenue } from '../../hooks/useVenue';
import toastService from '../../services/toastService';
import styles from './SeatSheet.module.css';

export interface SeatInfo {
  section: string;
  row: string;
  seat: string;
}

export interface SeatSheetProps {
  isOpen: boolean;
  initialSeat: SeatInfo;
  dismissable?: boolean;
  subtitle?: string;
  onSaved?: (seat: SeatInfo) => void;
  onCancel: () => void;
}

const SeatSheet: React.FC<SeatSheetProps> = ({
  isOpen,
  initialSeat,
  dismissable = true,
  subtitle,
  onSaved,
  onCancel,
}) => {
  const { user, updateUser } = useAuth();
  const { sections: venueSections } = useVenue();
  const [section, setSection] = useState(initialSeat.section);
  const [row, setRow] = useState(initialSeat.row);
  const [seat, setSeat] = useState(initialSeat.seat);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setSection(initialSeat.section);
      setRow(initialSeat.row);
      setSeat(initialSeat.seat);
      setError('');
    }
  }, [isOpen, initialSeat.section, initialSeat.row, initialSeat.seat]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && dismissable) onCancel();
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, onCancel, dismissable]);

  const hasSectionList = venueSections.length > 0;
  const filteredSections = useMemo(() => {
    if (!hasSectionList) return [];
    const query = section.trim().toUpperCase();
    if (!query) return venueSections.slice(0, 12);
    return venueSections.filter((s) => s.includes(query)).slice(0, 12);
  }, [hasSectionList, section, venueSections]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const trimmed = {
      section: section.trim().toUpperCase(),
      row: row.trim().toUpperCase(),
      seat: seat.trim(),
    };

    if (!trimmed.section || !trimmed.row || !trimmed.seat) {
      setError('Please enter section, row, and seat.');
      return;
    }

    setIsSaving(true);
    try {
      const result = await authService.updateProfile(
        user?.name || 'Customer',
        trimmed.section,
        trimmed.row,
        trimmed.seat
      );
      updateUser({
        section: result.user.section,
        row: result.user.row,
        seat: result.user.seat,
        seatConfirmedAt: new Date().toISOString(),
      });
      toastService.success('Seat updated');
      onSaved?.({
        section: result.user.section,
        row: result.user.row,
        seat: result.user.seat,
      });
      onCancel();
    } catch {
      setError('Failed to save seat. Please try again.');
      toastService.error('Failed to save seat');
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className={styles.overlay}
      role="presentation"
      onClick={dismissable ? onCancel : undefined}
    >
      <div
        className={styles.sheet}
        role="dialog"
        aria-modal="true"
        aria-labelledby="seat-sheet-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.sheetHandle} aria-hidden="true" />
        <div className={styles.header}>
          <h2 id="seat-sheet-title" className={styles.title}>Your seat</h2>
          {dismissable && (
            <button type="button" className={styles.closeButton} onClick={onCancel} aria-label="Close">
              ×
            </button>
          )}
        </div>
        <p className={styles.subtitle}>
          {subtitle ?? "We'll deliver food directly to your seat in the stadium."}
        </p>

        <form className={styles.form} onSubmit={handleSave}>
          <div className={styles.inputGroup}>
            <label className={styles.label} htmlFor="seat-section">Section</label>
            {hasSectionList ? (
              <>
                <input
                  id="seat-section"
                  className={styles.input}
                  value={section}
                  onChange={(e) => setSection(e.target.value.toUpperCase())}
                  placeholder="Search sections…"
                  list="seat-section-options"
                  autoFocus
                />
                <datalist id="seat-section-options">
                  {venueSections.map((s) => (
                    <option key={s} value={s} />
                  ))}
                </datalist>
                {filteredSections.length > 0 && section && !venueSections.includes(section.trim().toUpperCase()) && (
                  <div className={styles.sectionSuggestions}>
                    {filteredSections.map((s) => (
                      <button
                        key={s}
                        type="button"
                        className={styles.sectionChip}
                        onClick={() => setSection(s)}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <input
                id="seat-section"
                className={styles.input}
                value={section}
                onChange={(e) => setSection(e.target.value.toUpperCase())}
                placeholder="e.g. 112"
                autoFocus
              />
            )}
          </div>
          <div className={styles.inputGroup}>
            <label className={styles.label} htmlFor="seat-row">Row</label>
            <input
              id="seat-row"
              className={styles.input}
              value={row}
              onChange={(e) => setRow(e.target.value.toUpperCase())}
              placeholder="e.g. C"
            />
          </div>
          <div className={styles.inputGroup}>
            <label className={styles.label} htmlFor="seat-number">Seat</label>
            <input
              id="seat-number"
              className={styles.input}
              value={seat}
              onChange={(e) => setSeat(e.target.value)}
              placeholder="e.g. 14"
            />
          </div>

          {error && <p className={styles.error}>{error}</p>}

          <button type="submit" className={styles.saveButton} disabled={isSaving}>
            {isSaving ? 'Saving…' : 'Save seat'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default SeatSheet;
